/* eslint-disable max-len */
/* eslint-disable import/no-cycle */
/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable react/no-array-index-key */
import React, { useState, useEffect, forwardRef, useImperativeHandle, useCallback } from 'react';
import { Formik, Form } from 'formik';
import { useNavigate } from 'react-router-dom';
import BootstrapSpinner from 'shared/components/bootstrap-spinner/BootstrapSpinner';
import MarketSpendFields from './MarketSpendFields';
import Button from '../../../shared/components/button/Button';
import {
  getTotalPercent,
  replaceWithInt,
  transformMarkets, transformWeeklySpends,
} from '../helpers/helpers';
import useStore from '../hooks/useCampaignStore';
import {marketSpendFormSchema, invalidTotal, invalidTotalMarket} from '../helpers/formSchema';
import Dialog from '../../Dialogs/Dialog';
import { updateMarketDistribution, notifyStations, getGroupName, getGroupNameMarketsAndStations, getExistingGroupNameMarkets, updateAllRollup } from '../requests';
import useHandleUnsavedForm from '../hooks/useHandleUnsavedForm';
import UnsaveFormWarning from '../Dialog/UnsaveFormWarning';
import { preSubmitForm } from '../helpers/form';
import useSidenavStore from '../../../shared/sidebar/hooks/useSidenavStore';
import useDirtyForm from '../hooks/useDirtyForm';
import { useCallbackPrompt } from 'components/_reusable/blocker/UsePromptComponent.ts';
import ConfirmLeaveModal from 'shared/components/confirm-leave-modal/ConfirmLeaveModal';
import NotifyStationModal from '../Dialog/NotifyStationModal';
import Select from 'shared/components/select/Select';
import { pencilGreenIcon } from 'assets';
import GroupNameForm from './components/GroupNameForm';
import MarketGroupStationForm from './components/MarketGroupStationForm';
import { OpButton } from 'op2mise-react-widgets';
import { CampaignObjectiveEnum, marketScopeSwitchOptions } from '../CampaignConstants';
import clsx from 'clsx';
import AuthService from 'core/auth.service';

const MarketSpendForm = forwardRef(({}, ref) => {
  const {
    setShowCampaignForms,
    markets,
    setMarkets,
    isFormTouched,
    setIsFormTouched,
    setActiveFormId,
    selectedCampaign,
    setIsFormSaved,
    isMarketSplitSaved,
    setIsMarketSplitSaved,
    isFormSaved,
    changeFormTab,
    setChangeFormTab,
    setNewCampaign,
    newCampaign,
    currencyCode,
    setWeeklySpendDates,
    isCampaignActive,
    newCampaignSentNotification,
    setNewCampaignSentNotification,
  } = useStore((state) => state);
  const { toPath } = useSidenavStore((state) => state);
  const navigate = useNavigate();

  const userIdentity = AuthService.getUserIdentity();
  const isOrganisationAgency = userIdentity.organisationType === 2;

  const [marketSpendList, setMarketSpendList] = useState(null);
  const [customErrorMsg, setCustomErrorMsg] = useState('');
  const [proceedAndSave, setProceedAndSave] = useState(false);
  const [isMarketSplitSaving, setIsMarketSplitSaving] = useState(false);
  const [isFetchingClientGroup, setIsFetchingClientGroup] = useState(false);
  const [groupNameInitialRender, setGroupNameInitialRender] = useState(true);

  // Modal States
  const [showDialog, setShowDialog] = useState(false);
  const [showStationNotificationModal, setShowStationNotificationModal] = useState(false);
  const [noActiveContactMessage, setNoActiveContactMessage] = useState('');
  const [isNotficationSending, setIsNotificationSending] = useState(false);
  const [showGroupNameForm, setShowGroupNameForm] = useState(false);
  const [showMarketForm, setShowMarketForm] = useState(false);

  const [selectedGroupName, setSelectedGroupName] = useState(null);
  const [selectedMarket, setSelectedMarket] = useState(null);
  const [selectedMarketScope, setSelectedMarketScope] = useState(marketScopeSwitchOptions[0]);
  const isEditGroupDisabled = !isCampaignActive || !selectedGroupName?.name;

  const [groupNameMarkets, setGroupNameMarkets] = useState(null);
  const [groupNameList, setGroupNameList] = useState([]);
  const [stationIds, setStationIds] = useState([]);
  // TODO: Update state name as this is only tracking markets without active stations
  const [marketsWithoutBudget, setMarketsWithoutBudget] = useState([]);

  // Collapse States
  const [expandedId, setExpandedId] = useState(null);
  const [expandedIds, setExpandedIds] = useState([]);
  
  useHandleUnsavedForm(setShowDialog);

  const { handleProceed, handleCancel, handleDiscard } = useDirtyForm({
    setShowDialog,
    setProceedAndSave,
  });

  // Dirty forms
  const [showConfirmLeaveModal, setShowConfirmLeaveModal] = useState(false);
  const [showPrompt, confirmNavigation, cancelNavigation, handleHideNavigation] = useCallbackPrompt(
    isFormTouched
  );
  const [shouldExecuteSaveAndProceed, setShouldExecuteSaveAndProceed] = useState([false, () => {}]);
  const [proceedToNavigation, setProceedToNavigation] = useState(false);

  useImperativeHandle(ref, () => ({
    submitForm(callbackFunction) {
      setShouldExecuteSaveAndProceed([true, callbackFunction]);
    },
  }));

  const handleNavigate = (proceed) => {
    if (!showConfirmLeaveModal && !shouldExecuteSaveAndProceed[0]) confirmNavigation();
    else {
      if (shouldExecuteSaveAndProceed[0]) {
        shouldExecuteSaveAndProceed[1]();
        setShouldExecuteSaveAndProceed([false, () => {}]);
      } else {
        setShowConfirmLeaveModal(false);
        if (proceed) handleClose(true);
      }
    }
  };

  // Market Spend Form Submission Logic
  const onSubmit = async (values, { setErrors }) => {
    const totalAllocation = Number(getTotalPercent(values.markets, 'allocation').toFixed(2));
    const maxTotalAllocation = 100.99;
    const minTotalAllocation = 99.0;
    const isCampaignObjectivePlaced =
      selectedCampaign.objective === CampaignObjectiveEnum.PLACED;

    const hasTotalAllocationError = isCampaignObjectivePlaced
      ? totalAllocation > maxTotalAllocation
      : totalAllocation < minTotalAllocation ||
        totalAllocation > maxTotalAllocation;

    setIsMarketSplitSaving(true);
    if (hasTotalAllocationError) {
      const { errors, msg } = invalidTotalMarket(values.markets, 'allocation');
      setCustomErrorMsg(isCampaignObjectivePlaced
        ? "Total cannot exceed 100%"
        : msg
      );
      setErrors({ markets: errors });
      setIsMarketSplitSaving(false);
      return;
    }

    const marketIdsWithoutStations = [];

    const checkActiveStationInEachMarket = (markets, updatedStations) => {
      for (const market of markets) {
        if (market.clientGroupMarketStations.length === 0)  {
          marketIdsWithoutStations.push(market.marketId);
          continue;
        }; // Market with no stations is invalid
    
        const hasActiveStation = market.clientGroupMarketStations.some((station) => {
          const updatedStation = updatedStations.find(
            (upd) => upd.stationId === station.stationId && upd.marketId === market.marketId
          );
    
          return updatedStation ? updatedStation.active : station.active;
        });
    
        if (!hasActiveStation) {
          marketIdsWithoutStations.push(market.marketId)
        }else { 
          continue;
        }
      }
    };

    // Check if campaign objective is REACH
    if(selectedCampaign.objective === CampaignObjectiveEnum.REACH) {
      const marketsWithReach = values.markets.filter((m => m.reach > 0));
      checkActiveStationInEachMarket(marketsWithReach, stationIds);
      setMarketsWithoutBudget(marketIdsWithoutStations);
    }else {
      // Only check the budge otherwise
      const marketsWithBudget = values.markets.filter((m => m.budgetAmount > 0));
      checkActiveStationInEachMarket(marketsWithBudget, stationIds);
      setMarketsWithoutBudget(marketIdsWithoutStations);
    }

    if(marketIdsWithoutStations.length > 0) {
      setCustomErrorMsg("At least one station should be selected on a market");
      setIsMarketSplitSaving(false);
      return;
    }
    setCustomErrorMsg('');

    const data = {
      marketDistributionParams: values.markets.map((val) => ({
        campaignId: selectedCampaign.id,
        marketId: val.marketId,
        budget: '$',
        allocation: parseFloat(val.allocation || 0).toFixed(2),
        budgetAmount: parseFloat(val.budgetAmount || 0).toFixed(0),
        frequency: val.frequency || 0,
        reach: val.reach || 0,
        costPerPoint: parseFloat(val.costPerPoint || 0).toFixed(0),
        targetRating: parseFloat(val.targetRating || 0).toFixed(0),
      })),
      clientGroupId: selectedGroupName.id,
      organisationScopeId: selectedMarketScope.id,
      stationParams: stationIds.map((item) => ({id: item.stationId, active: item.active, marketId: item.marketId})) || []
    };
    const campaignId = selectedCampaign.id;

    const res = await updateMarketDistribution(data, selectedCampaign.id);
    await updateAllRollup(campaignId);
    if (res.statusCode === 200) {
      setIsFormSaved(true);
      setMarkets(res.value.marketDistributionResults);
      setWeeklySpendDates(
          transformWeeklySpends(res.value.weeklyDistributionResults),
      );

      // if (!newCampaignSentNotification) {
      //   setShowStationNotificationModal(true);
      // } else {
      //   setShowDialog(true);
      // }

      // Temporarily show campaign has been updated modal for now
      setShowDialog(true);

      await handleGetExistingGroupNameMarketsAndStations([selectedGroupName]);


      setIsFormTouched(false);
      if (proceedToNavigation) setNewCampaign(false);
    } else {
      handleNavigate(false);
    }
    setIsMarketSplitSaved(true);
    setIsFormSaved(true);
    setChangeFormTab(false);
    setIsMarketSplitSaving(false);
  };

  // Market Spend Form Close Logic
  const handleClose = (proceedFromNavigation) => {
    if (isFormTouched && !proceedFromNavigation) {
      setShowConfirmLeaveModal(true);
    } else {
      setIsFormSaved(true);
      setIsFormTouched(false);
      setShowCampaignForms(false);
      setNewCampaign(false);
      setIsMarketSplitSaved(false);
    }
  };

  // Modal Helper Functions
  const handleCloseDialog = () => {
    setShowDialog(false);
    setShowStationNotificationModal(false);
    setNoActiveContactMessage('');
    setNewCampaignSentNotification(true);
    if (proceedToNavigation) handleNavigate(true);
    if (newCampaign && !proceedToNavigation) setActiveFormId('weeklySpend');
  };

  const handleModalSendNotification = async () => {
    let notifyStationData = {
      campaignId: selectedCampaign.id,
      isNewCampaign: true,
    };

    setIsNotificationSending(true);
    try {
      const res = await notifyStations(notifyStationData, selectedCampaign.id)
      setShowStationNotificationModal(false);
      setChangeFormTab(newCampaign);
      setIsNotificationSending(false);
      setNewCampaignSentNotification(true);
      if (proceedToNavigation) handleNavigate(true);
      if (newCampaign && !proceedToNavigation) setActiveFormId('weeklySpend');
    } catch (error) {
      console.error(error);
      setShowStationNotificationModal(false);
      setIsNotificationSending(false);
      if (error.response.data.message && error.response.status === 400) {
        setNoActiveContactMessage(error.response.data.message);
      }
    }
  };

  const handleGetGroupNames = async (marketScope) => {
    try {
      const res = await getGroupName(selectedCampaign.type, marketScope.id);
      const defaultGroupName = res?.value?.find((item) => item.id === 0);

      setGroupNameList(res?.value);
      setSelectedGroupName(defaultGroupName);
      await handleGetGroupNameMarketsAndStations(defaultGroupName.id, marketScope);
    } catch (error) {
      console.error(error);
    }
  }

  const handleGetGroupNameMarketsAndStations = async (groupNameId, marketScope) => {
    try {
      setIsFetchingClientGroup(true);
      const res = await getGroupNameMarketsAndStations(
        groupNameId,
        selectedCampaign.id,
        marketScope.id
      );
      setGroupNameMarkets(res?.clientGroupMarkets);    
    } catch(error) {
      console.error(error);
    } finally {
      setIsFetchingClientGroup(false);
    }
  }

  const handleGetExistingGroupNameMarketsAndStations = async () => {
    try {
      setIsFetchingClientGroup(true);
      const res = await getExistingGroupNameMarkets(selectedCampaign.id);
      const groupNameRes = await getGroupName(
        selectedCampaign.type,
        res?.organisationScopeId
      );
      const groupName = groupNameRes.value.find(
        (item) => item.id === res?.clientGroupId
      );
      const scope = isOrganisationAgency
        ? marketScopeSwitchOptions.find(
            (item) => item.id === res?.organisationScopeId
          )
        : marketScopeSwitchOptions[0];

      setGroupNameList(groupNameRes?.value);
      setSelectedGroupName(groupName);
      setSelectedMarketScope(scope);
      setGroupNameMarkets(res?.clientGroupMarkets);
    } catch(error) {
      console.error(error);
    } finally {
      setIsFetchingClientGroup(false);
    }
  };

  const handleInitialRender = async () => {
    if (newCampaign && !isMarketSplitSaved) {
      await handleGetGroupNames(selectedMarketScope);
    }else {
      await handleGetExistingGroupNameMarketsAndStations();
    }

    setGroupNameInitialRender(false);
  };
  
  // Expand/Collapse Action
  const toggleExpand = useCallback((id) => {
    setExpandedId((prevExpandedId) => (prevExpandedId === id ? null : id));
    // Make sure the array has no duplicate values
    setExpandedIds((prevIds) => Array.from(new Set([...prevIds, id])));
  }, []) 

  useEffect(() => {
    // If campaign is new, initiate dirty forms
    if (newCampaign) setIsFormTouched(true);

    handleInitialRender();
  }, []);

  useEffect(() => {
    if (!!groupNameMarkets && !!selectedCampaign) {
      setMarketSpendList(transformMarkets(groupNameMarkets, selectedCampaign));
    }
  }, [groupNameMarkets, selectedCampaign]);

  // Combine all the station of each market on an array
  useEffect(() => {
    if(groupNameMarkets) {
      const currentStations = groupNameMarkets?.flatMap((m) => m.clientGroupMarketStations.map(st => ({stationId: st.stationId, active: st.active, marketId: m.marketId,})))

     setStationIds(currentStations);
    }
   }, [groupNameMarkets, selectedGroupName])

  return (
    <div className="d-flex w-100 gap-2 default-text" key={marketSpendList}>
      <Formik
        initialValues={{ markets: marketSpendList }}
        validationSchema={marketSpendFormSchema}
        onSubmit={onSubmit}
        validateOnChange={false}
        validateOnBlur={false}
      >
        {({
          values,
          errors,
          handleChange: onChange,
          setFieldValue,
          submitForm,
          touched,
          setTouched,
          validateForm,
          setErrors,
        }) => {

          // Update formik initial value
          useEffect(() => {
            setFieldValue('markets', marketSpendList); 
          }, [marketSpendList, setFieldValue]);

          const handleChange = (e) => {
            setTouched({ ...touched, [e.target.name]: e.target.value });
            setIsFormSaved(true);
            setIsFormTouched(true);
            setIsFormSaved(true);
            e.target.value = replaceWithInt(e.target.value);
            onChange(e);
          };

          const handleOnValidate = (proceedFromDirtyForm) => {
            preSubmitForm({
              setIsFormSaved,
              validateForm,
              setErrors,
              setChangeFormTab,
              setProceedAndSave,
              showConfirmLeaveModal,
              setShowConfirmLeaveModal,
              cancelNavigation,
              proceedFromDirtyForm,
              submitForm,
            });
          };

          const handleOnHide = () => {
            if (showConfirmLeaveModal) setShowConfirmLeaveModal(false);
            else cancelNavigation();
          };
          
          const handleOnDiscard = () => {
            if (showConfirmLeaveModal) handleClose(true);
            else {
              setNewCampaign(false);
              confirmNavigation();
            }
            setIsFormTouched(false);
            setProceedToNavigation(true);
          };
        
          const handleOnSaveAndProceed = () => {
            handleOnValidate(true);
            setProceedToNavigation(true);
            handleHideNavigation();
          };

          const handleClearMarketSpend = () => {
            if (!isCampaignActive) return;

            const updatedMarkets = values.markets.map(item => ({
              ...item,
              allocation: 0,
              budgetAmount: 0,
              targetRating: selectedCampaign?.objective === 1 ? item.targetRating : 0,
            }));
            setFieldValue('markets', updatedMarkets);
            setIsFormTouched(true);
          };

          const handleOnSelectGroup = async (data, proceedFromDirtyForm) => {
            handleOnResetForm();
              setIsFormTouched(true);
              setSelectedGroupName(data);
              await handleGetGroupNameMarketsAndStations(data.id, selectedMarketScope);
          };

          const handleOnSelectMarketScope = (data, proceedFromDirtyForm) => {
            if (!isCampaignActive) return;

            handleOnResetForm();
            setIsFormTouched(true);
            setSelectedMarketScope(data);
            handleGetGroupNames(data);
          };

          const handleOnResetForm = () => {
            setCustomErrorMsg('');
            setMarketsWithoutBudget([]);
          };

          useEffect(() => {
            if (shouldExecuteSaveAndProceed[0]) handleOnSaveAndProceed();
          }, [shouldExecuteSaveAndProceed]);


          return (
            <Form
              className="w-100 d-flex flex-column gap-2 form"
              noValidate
              // prevent the form from submitting by hitting enter upon searching/filtering on Datagrid
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                }
              }} 
            >
              <NotifyStationModal
                show={showStationNotificationModal}
                content={
                  <p className="text-center">Campaign has been successfully created.<br/>
                  Would you like to notify the stations?</p>
                }
                onClose={handleCloseDialog}
                onProceed={handleModalSendNotification}
                isActionPending={isNotficationSending}
              />
              <Dialog
                show={noActiveContactMessage}
                onClose={handleCloseDialog}
              >
                {noActiveContactMessage}
              </Dialog>
              <Dialog
                onClose={handleCloseDialog}
                show={showDialog}
                Content={
                      changeFormTab && !isFormSaved ? (
                        <UnsaveFormWarning
                          handleProceed={() => handleProceed(submitForm, validateForm, setErrors)}
                          onCancel={handleCancel}
                          handleDiscard={handleDiscard}
                        />
                      ) : null
                    }
                isDirtyForm={changeFormTab && !isFormSaved}
              >
                Campaign {selectedCampaign?.name} has been updated.
              </Dialog>
              <div className="min-max-h-20 d-flex justify-content-center campagin-form customScroll">
                <div className="d-flex flex-column gap-10" style={{ width: '100%'}}>
                  <div className="d-flex gap-2 align-items-center" style={{ width: '100%'}}>
                    {/* Market Split Header */}
                    <div className="border-bottom d-flex gap-2" style={{ width: '99%'}}>
                      <div className="d-inline-flex" style={{ width: '100%'}}>
                        <div className="d-inline-flex gap-2 align-items-start" style={{ width: '100%'}}>
                        <div className="d-inline-flex" style={{ width: '30%'}}>
                        <span className="labelHeader">
                        Total market spend:
                          {' '}
                          {getTotalPercent(values.markets, 'allocation')}
                          %
                        </span>
                        </div>
                          <div className="d-inline-flex justify-content-center" style={{ width: '12%'}}>
                            <span className="labelHeader">
                              %
                            </span>
                          </div>
                          <div className="d-inline-flex justify-content-center" style={{ width: '12%'}}>
                            <span className="labelHeader">
                              Budget
                            </span>
                          </div>
                          <div className="d-inline-flex justify-content-center" style={{ width: '12%'}}>
                            <span className="labelHeader">
                              Rating
                            </span>
                          </div>
                          <div className="d-inline-flex justify-content-center" style={{ width: '12%'}}>
                            <span className="labelHeader">
                              Reach
                            </span>
                          </div>
                          <div className="d-inline-flex justify-content-center" style={{ width: '12%'}}>
                            <span className="labelHeader">
                              CPP
                            </span>
                          </div>
                          <div className="d-inline-flex justify-content-center" style={{ width: '12%'}}>
                            <span className="labelHeader">
                              Frequency
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>


                  {/* Clear Market Split Clear Button */}

                  <div className="d-flex align-items-center" style={{ width: '100%', marginTop: '20px'}}>
                    {/* Custom Error Message */}
                    <div className="d-flex gap-2" style={{ width: '30.3%'}} >
                      {customErrorMsg && (
                          <span className="is-danger">{customErrorMsg}</span>
                      )}
                    </div>

                    <div style={{ width: '69.7%'}}>
                      <span
                        className={
                          (!isCampaignActive)
                            ? 'clear-spend-button-disabled'
                            : 'clear-spend-button'
                        }
                        onClick={handleClearMarketSpend}
                      >
                        Clear all market spend
                      </span>
                    </div>
                  </div>

                  {!groupNameInitialRender && (
                    <div className={'d-flex mt-3 align-items-center'}>
                      <span className="select-label">Group:</span>
                      <Select
                          list={groupNameList}
                          noSearch={false}
                          onSelect={handleOnSelectGroup}
                          text={selectedGroupName?.name || "Unavailable group - not found"}
                          width="300px"
                          disabled={!isCampaignActive}
                      />
                      {isOrganisationAgency && (
                        <div className={`market-spend-scope-switch-parent ${!isCampaignActive && 'disabled'}`}>
                          {marketScopeSwitchOptions.map((item) => (
                            <div
                              title={item.label}
                              key={item.label}
                              onClick={() => handleOnSelectMarketScope(item)}
                              className={clsx({
                                'market-spend-scope-switch-container': true,
                                'market-spend-scope-switch-active': selectedMarketScope?.id === item.id,
                                'market-spend-scope-switch-inactive': selectedMarketScope?.id !== item.id,
                              })}
                            >
                              <span>{item.label}</span>
                            </div>
                          ))}
                        </div>
                      )}
                      <img
                        alt="edit-group"
                        style={{ marginLeft: '15px', cursor: !isEditGroupDisabled ? 'pointer' : 'not-allowed' }}
                        onClick={() => {
                          if (isEditGroupDisabled) return;
                          setIsFormTouched(true);
                          setShowGroupNameForm(true)
                        }}
                        className={`action-icon ${isEditGroupDisabled ? 'disabled' : ''}`}
                        src={pencilGreenIcon}
                        title="Edit group"
                      />
                    </div>
                  )}

                  {/* Market Split Fields */}

                  <div className="d-flex flex-column gap-3 market-form-container min-max-h-20 customScroll" style={{ width: '100%'}}>
                    {isFetchingClientGroup ? (
                      <BootstrapSpinner />
                    ) : (
                      <>
                        {marketSpendList?.map((market, key) => (
                          <MarketSpendFields
                            key={`${market.distribution}-${key}`}
                            budget={selectedCampaign?.budget}
                            setFieldValue={setFieldValue}
                            index={key}
                            {...market}
                            values={values.markets}
                            errors={errors}
                            objective={selectedCampaign?.objective}
                            onChange={handleChange}
                            rating={selectedCampaign?.rating}
                            isCampaignActive={isCampaignActive}
    
                            // Collapse And Modal States
                            toggleExpand={toggleExpand}
                            expandedId={expandedId}
                            expandedIds={expandedIds}
                            newCampaign={newCampaign}
                            setShowMarketForm={setShowMarketForm}
                            stationIds={stationIds}
                            setStationIds={setStationIds}
                            setSelectedMarket={setSelectedMarket}
                            setGroupNameMarkets={setGroupNameMarkets}
                            setIsFormTouched={setIsFormTouched}
                            marketsWithoutBudget={marketsWithoutBudget}
                          />
                        ))}
                      </>
                    )}
                  </div>
                </div>
              </div>

             {/* Group Name Modal */}
             {selectedGroupName && showGroupNameForm && (
               <GroupNameForm
                  groupName={selectedGroupName}
                  show={showGroupNameForm}
                  selectedCampaign={selectedCampaign}
                  selectedMarketScope={selectedMarketScope}
                  marketSpendList={values.markets}
                  setGroupNameMarkets={setGroupNameMarkets}
                  onHide={() => {
                    setShowGroupNameForm(false);
                  }}
                  setIsFetchingClientGroup={setIsFetchingClientGroup}
               />
             )}

             {/* Market Modal */}
             {selectedMarket && showMarketForm && (
               <MarketGroupStationForm
                  groupName={selectedGroupName}
                  isOrganisationAgency={isOrganisationAgency}
                  market={selectedMarket}
                  stationIds={stationIds}
                  selectedCampaign={selectedCampaign}
                  groupNameMarkets={values.markets}
                  setGroupNameMarkets={setGroupNameMarkets}
                  show={showMarketForm}
                  onHide={() => {
                    setSelectedMarket(null);
                    setShowMarketForm(false);
                  }}
                  setIsFetchingClientGroup={setIsFetchingClientGroup}
               />
             )}

              <div className="form-btn-container align-items-center d-flex gap-4 mt-5 justify-content-end" style={{ width: '95%' }}>
                <div>
                  <Button
                    text="Close"
                    onClick={() => handleClose(false)}
                    defaultBtn
                  />
                </div>
                <div>
                  <OpButton 
                    label={'Save'}
                    type="submit" 
                    onClick={() => {handleOnValidate(false)}}
                    disabled={!isCampaignActive}
                    loading={isMarketSplitSaving} 
                  />
                </div>
              </div>

              <ConfirmLeaveModal
                show={(showPrompt || showConfirmLeaveModal) && (!showStationNotificationModal && !showDialog)}
                onHide={handleOnHide}
                onSave={() => handleOnSaveAndProceed(handleOnValidate)}
                onDiscard={handleOnDiscard}
              />
            </Form>
          );
        }}
      </Formik>
    </div>
  );
})

export default MarketSpendForm;
